import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function OrganizationAssignment() {
  const [data, setData] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [targetOrg, setTargetOrg] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [stateFilter, setStateFilter] = useState("all");
  const [suggFilter, setSuggFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/persons/unassigned-organization", { params: { q, page_size: 300 } });
      setData(data); setOrgs(data.organizations || []); setSelected({});
    } catch (e) { toast.error(apiError(e)); setData({ persons: [], total: 0 }); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const allPersons = data?.persons || [];
  const classOptions = Array.from(new Set(allPersons.flatMap((p) => p.classifications || []))).sort();
  const persons = allPersons.filter((p) => {
    if (stateFilter !== "all" && (p.state || "").toUpperCase() !== stateFilter.toUpperCase()) return false;
    if (classFilter !== "all" && !(p.classifications || []).includes(classFilter)) return false;
    if (suggFilter !== "all") {
      const sug = p.suggested_organization?.organization_id || "none";
      if (suggFilter === "none" ? sug !== "none" : sug !== suggFilter) return false;
    }
    return true;
  });
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = persons.length > 0 && persons.every((p) => selected[p.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(persons.map((p) => [p.id, true])));
  };

  const doAssign = async () => {
    if (!targetOrg) return toast.error("Choose an organization");
    if (selectedIds.length === 0) return toast.error("Select at least one person");
    setBusy(true);
    try {
      const { data } = await api.post("/persons/assign-organization", { person_ids: selectedIds, organization_id: targetOrg, reason: reason || null });
      toast.success(`Assigned ${data.assigned} people to ${data.organization_name}`);
      setConfirm(false); setReason(""); setTargetOrg(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const assignOne = (p, orgId) => {
    setSelected({ [p.id]: true }); setTargetOrg(orgId); setConfirm(true);
  };

  return (
    <div data-testid="org-assignment-page">
      <PageHeader title="Organization Assignment" description="Assign imported People to an Organization so recruiter visibility works. Suggestions are advisory only — nothing is auto-assigned; you confirm every change." />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, city or state" className="w-64" data-testid="org-search" />
        <div className="w-40">
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger data-testid="org-state-filter"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {(data?.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Select value={suggFilter} onValueChange={setSuggFilter}>
            <SelectTrigger data-testid="org-suggested-filter"><SelectValue placeholder="Suggested Org" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suggestions</SelectItem>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>Suggested: {o.name}</SelectItem>)}
              <SelectItem value="none">No suggestion</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger data-testid="org-class-filter"><SelectValue placeholder="Classification" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classifications</SelectItem>
              {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        {selectedIds.length > 0 && (
          <>
            <span className="text-sm text-slate-500" data-testid="org-selected-count">{selectedIds.length} selected</span>
            <div className="w-56">
              <Select value={targetOrg} onValueChange={setTargetOrg}>
                <SelectTrigger data-testid="org-target-select"><SelectValue placeholder="Assign to organization…" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => targetOrg ? setConfirm(true) : toast.error("Choose an organization")} data-testid="org-bulk-assign" className="bg-[#006090] hover:bg-[#004E75]">Assign Selected</Button>
          </>
        )}
      </div>

      {data === null ? <PageLoader /> : persons.length === 0 ? (
        <EmptyState icon={Building2} title="No unassigned People" description="Everyone has an Organization assignment." />
      ) : (
        <div className="overflow-x-auto tr-scroll rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="org-assignment-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="py-2.5 px-3"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="org-check-all" /></th>
              <th className="py-2.5 px-3">Person</th><th className="py-2.5 px-3">Person ID</th><th className="py-2.5 px-3">Import ID</th><th className="py-2.5 px-3">Classifications</th>
              <th className="py-2.5 px-3">City</th><th className="py-2.5 px-3">State</th><th className="py-2.5 px-3">Source</th>
              <th className="py-2.5 px-3">Recruiter</th><th className="py-2.5 px-3">Suggested Org</th><th className="py-2.5 px-3"></th>
            </tr></thead>
            <tbody>
              {persons.map((p) => (
                <tr key={p.id} className="border-b border-slate-100" data-testid={`org-row-${p.id}`}>
                  <td className="py-2.5 px-3"><Checkbox checked={!!selected[p.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: !!v }))} data-testid={`org-check-${p.id}`} /></td>
                  <td className="py-2.5 px-3 text-slate-800">{p.first_name} {p.last_name}</td>
                  <td className="py-2.5 px-3 text-slate-500">{p.human_id}</td>
                  <td className="py-2.5 px-3 text-slate-400 max-w-[8rem] truncate" title={p.person_import_id}>{p.person_import_id || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{(p.classifications || []).join(", ") || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{p.city || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{p.state || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{p.source || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500">{p.recruiter_name || "—"}</td>
                  <td className="py-2.5 px-3">
                    {p.suggested_organization ? (
                      <span className="inline-flex items-center gap-1 text-sky-700" title={p.suggested_organization.reason} data-testid={`org-suggestion-${p.id}`}>
                        <Sparkles className="w-3.5 h-3.5" /> {p.suggested_organization.organization_name}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    {p.suggested_organization && (
                      <Button size="sm" variant="outline" data-testid={`org-accept-suggestion-${p.id}`}
                        onClick={() => assignOne(p, p.suggested_organization.organization_id)}>Use suggestion</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-white" data-testid="org-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm Organization Assignment</DialogTitle>
            <DialogDescription>Assign {selectedIds.length} {selectedIds.length === 1 ? "person" : "people"} to <strong>{orgs.find((o) => o.id === targetOrg)?.name || "—"}</strong>. This is audited and changes recruiter visibility.</DialogDescription></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="org-assign-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button onClick={doAssign} disabled={busy} data-testid="org-assign-confirm" className="bg-[#006090] hover:bg-[#004E75]">Confirm Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
